import ClearIcon from "@mui/icons-material/Clear";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Alert,
  AlertTitle,
  Box,
  LinearProgress,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { FullScreenDocumentPreview, extensionFromName } from "components/Document/shared";
import { useCheckForFile } from "hooks/useCheckForFile";
import { useCallback, useMemo, useState, type MouseEvent } from "react";
import { downloadFromUrl } from "util/download";

import {
  formatScoutFileSecondaryText,
  inferScoutFileDetectedLabel,
} from "../../scoutAttachmentLimits";
import { ScoutFileThumbnailContent } from "../ScoutFileThumbnailContent";
import { ScoutFilePart } from "./scoutChatMessage";

const UPLOADED_FILE_URL_PREFIX = "encamp-upload://";

function parseUploadStorageKey(url: string | undefined): string | undefined {
  if (!url?.startsWith(UPLOADED_FILE_URL_PREFIX)) {
    return undefined;
  }
  const storageKey = url.slice(UPLOADED_FILE_URL_PREFIX.length);
  return storageKey.length > 0 ? storageKey : undefined;
}

type Props = {
  part: ScoutFilePart;
  onRemove?: () => void;
  state?: "default" | "error";
  errorText?: string;
};

export const ScoutFileDisplay = ({ part, onRemove, state = "default", errorText }: Props) => {
  const theme = useTheme();
  const alerts = useAlerts();
  const [previewUrl, setPreviewUrl] = useState<string>();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const { fetchFile } = useCheckForFile();

  const uploadStorageKey = useMemo(() => parseUploadStorageKey(part.url), [part.url]);
  const historySignedUrl = part.document?.signedDownloadUrl ?? undefined;
  const canAccessFile = historySignedUrl !== undefined || uploadStorageKey !== undefined;
  const downloadFilename = part.filename ?? part.title ?? "download";
  const fileExtension = extensionFromName(part.filename ?? part.title ?? "");
  const documentTypeLabel = inferScoutFileDetectedLabel(part.filename ?? part.title);
  const thumbnailUrl = part.document?.thumbnailSignedUrl ?? undefined;

  const resolveSignedPreviewUrl = useCallback(async (): Promise<string | undefined> => {
    if (historySignedUrl) {
      return historySignedUrl;
    }
    if (uploadStorageKey) {
      return (await fetchFile(uploadStorageKey)) ?? undefined;
    }
    return undefined;
  }, [fetchFile, historySignedUrl, uploadStorageKey]);

  const handleDownload = useCallback(
    async (event: MouseEvent) => {
      event.stopPropagation();
      if (!canAccessFile || isDownloading) {
        return;
      }

      setIsDownloading(true);
      try {
        const signedUrl = await resolveSignedPreviewUrl();
        if (!signedUrl) {
          alerts.error("Unable to download this file.");
          return;
        }
        await downloadFromUrl(signedUrl, downloadFilename);
      } catch {
        alerts.error("An error occurred while downloading the file.");
      } finally {
        setIsDownloading(false);
      }
    },
    [alerts, canAccessFile, downloadFilename, isDownloading, resolveSignedPreviewUrl]
  );

  const openPreview = useCallback(async () => {
    if (!canAccessFile || isPreviewLoading) {
      return;
    }
    if (previewUrl) {
      setIsPreviewOpen(true);
      return;
    }
    if (historySignedUrl) {
      setPreviewUrl(historySignedUrl);
      setIsPreviewOpen(true);
      return;
    }
    setIsPreviewLoading(true);
    try {
      const signedUrl = await resolveSignedPreviewUrl();
      if (!signedUrl) {
        return;
      }
      setPreviewUrl(signedUrl);
      setIsPreviewOpen(true);
    } finally {
      setIsPreviewLoading(false);
    }
  }, [canAccessFile, historySignedUrl, isPreviewLoading, previewUrl, resolveSignedPreviewUrl]);

  const isError = state === "error";
  if (isError) {
    return (
      <Alert
        severity="error"
        sx={{ width: "100%" }}
        action={
          onRemove ? (
            <Tooltip title="Remove invalid file">
              <IconButton
                edge="end"
                size="small"
                aria-label={`remove-file-${part.id}`}
                onClick={onRemove}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          ) : undefined
        }
      >
        <AlertTitle>{`Error: ${part.title ?? part.filename ?? "Attachment"}`}</AlertTitle>
        <Typography variant="body2">{errorText ?? "Upload failed."}</Typography>
      </Alert>
    );
  }

  const secondaryText = formatScoutFileSecondaryText({
    mediaType: part.mediaType ?? "application/octet-stream",
    sizeBytes: part.document?.sizeBytes,
    pageCount: part.document?.pageCount,
    ingestionMode: part.ingestionMode,
    extractionStatus: part.extractionStatus,
    extractionProgress: part.extractionProgress,
  });

  const showExtractionProgress =
    part.ingestionMode === "LARGE_FILE_EXTRACTION" &&
    (part.extractionStatus === "RUNNING" || part.extractionStatus === "PENDING");
  const pagesTotal = part.extractionProgress?.pagesTotal ?? 0;
  const pagesCompleted = part.extractionProgress?.pagesCompleted ?? 0;
  const progressValue =
    pagesTotal > 0 ? Math.min(100, Math.round((pagesCompleted / pagesTotal) * 100)) : undefined;

  return (
    <>
      <ListItem
        dense
        onClick={canAccessFile ? () => void openPreview() : undefined}
        sx={{
          paddingY: theme.spacing(2),
          bgcolor: theme.palette.encampBlue.light,
          borderRadius: "20px",
          minWidth: "440px",
          cursor: canAccessFile ? "pointer" : "default",
          opacity: isPreviewLoading || isDownloading ? 0.8 : 1,
          "& .scout-file-download-action": {
            opacity: isDownloading ? 1 : 0,
            transition: theme.transitions.create("opacity", {
              duration: theme.transitions.duration.shortest,
            }),
          },
          "&:hover .scout-file-download-action, &:focus-within .scout-file-download-action": {
            opacity: 1,
          },
        }}
        slotProps={{
          secondaryAction: {
            sx: {
              top: "50%",
              right: theme.spacing(2),
              transform: "translateY(-50%)",
            },
          },
        }}
        secondaryAction={
          <Stack direction="row" spacing={0.25} alignItems="center">
            {canAccessFile ? (
              <Tooltip title="Download file">
                <span className="scout-file-download-action">
                  <IconButton
                    edge="end"
                    size="small"
                    aria-label={`download-file-${part.id}`}
                    disabled={isDownloading}
                    onClick={(event) => void handleDownload(event)}
                    sx={{ p: 0.5 }}
                  >
                    <DownloadIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            {onRemove ? (
              <Tooltip title="Remove file">
                <IconButton
                  edge="end"
                  size="small"
                  aria-label={`remove-file-${part.id}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onRemove();
                  }}
                >
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            ) : null}
          </Stack>
        }
      >
        <ListItemIcon sx={{ minWidth: "unset", paddingRight: theme.spacing(2) }}>
          <Box
            sx={{
              width: 92,
              height: 120,
              borderRadius: 1,
              border: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <ScoutFileThumbnailContent thumbnailUrl={thumbnailUrl} fileExtension={fileExtension} />
          </Box>
        </ListItemIcon>
        <ListItemText
          primary={part.filename ?? "Attachment"}
          secondary={
            <Stack spacing={0.75} sx={{ mt: 0.25 }}>
              <Typography variant="body2" color="text.secondary" component="span">
                {secondaryText.documentInfo}
              </Typography>
              {secondaryText.extractionInfo ? (
                <Typography variant="body2" color="text.secondary" component="span">
                  {secondaryText.extractionInfo}
                </Typography>
              ) : null}
              {documentTypeLabel ? (
                <Typography variant="body2" color="text.secondary" component="span" sx={{ pt: 1 }}>
                  detected as {documentTypeLabel}
                </Typography>
              ) : null}
              {showExtractionProgress ? (
                <LinearProgress
                  variant={progressValue !== undefined ? "determinate" : "indeterminate"}
                  value={progressValue}
                  sx={{ height: 6, borderRadius: 1 }}
                />
              ) : null}
            </Stack>
          }
          slotProps={{ secondary: { component: "div" } }}
        />
      </ListItem>
      {previewUrl ? (
        <FullScreenDocumentPreview
          open={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          fileUrl={previewUrl}
          fileExtension={fileExtension}
          title={part.title ?? part.filename ?? "Attachment Preview"}
        />
      ) : null}
    </>
  );
};
